import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { Answer } from '../api/schemas';
import { isRunning, type Turn } from './conversation';

export type Conversation = { id: string; title: string; createdAt: number; updatedAt: number; turns: Turn[] };

type ChatState = {
  /** Newest activity first. */
  conversations: Conversation[];
  activeId: string | null;
  startNew: () => void;
  select: (id: string) => void;
  remove: (id: string) => void;
  /** Adds a turn to the active chat, starting one if needed; returns the chat's id. */
  addTurn: (turn: Turn) => string;
  updateTurn: (conversationId: string, turnId: string, fn: (t: Turn) => Turn) => void;
  saveDeskQuestion: (turnId: string, questionId: string) => void;
};

/** Chats as saved by an older build: answers may lack fields added since. */
type SavedChats = {
  activeId: string | null;
  conversations: (Omit<Conversation, 'turns'> & {
    turns: (Omit<Turn, 'answer'> & { answer: (Omit<Answer, 'panels'> & { panels?: Answer['panels'] }) | null })[];
  })[];
};

const MAX_SAVED = 30;
const TITLE_LENGTH = 60;

const titleFrom = (question: string) =>
  question.length > TITLE_LENGTH ? `${question.slice(0, TITLE_LENGTH).trimEnd()}…` : question;

// A reload cuts any stream mid-answer; save those turns as stopped rather than spinning forever.
const settle = (c: Conversation): Conversation => ({
  ...c,
  turns: c.turns.map((t) => (isRunning(t) ? { ...t, stopped: true } : t)),
});

/** Chats live in this browser only: there is no server-side chat history yet. */
export const useChatStore = create<ChatState>()(
  persist(
    (set, get) => ({
      conversations: [],
      activeId: null,
      startNew: () => { set({ activeId: null }); },
      select: (id) => { set({ activeId: id }); },
      remove: (id) => {
        set((s) => ({ conversations: s.conversations.filter((c) => c.id !== id), activeId: s.activeId === id ? null : s.activeId }));
      },
      addTurn: (turn) => {
        const { activeId, conversations } = get();
        const now = Date.now();
        const current = conversations.find((c) => c.id === activeId);
        const next: Conversation = current
          ? { ...current, updatedAt: now, turns: [...current.turns, turn] }
          : { id: crypto.randomUUID(), title: titleFrom(turn.question), createdAt: now, updatedAt: now, turns: [turn] };
        set({ activeId: next.id, conversations: [next, ...conversations.filter((c) => c.id !== next.id)] });
        return next.id;
      },
      updateTurn: (conversationId, turnId, fn) => {
        set((s) => ({
          conversations: s.conversations.map((c) =>
            c.id === conversationId ? { ...c, turns: c.turns.map((t) => (t.id === turnId ? fn(t) : t)) } : c,
          ),
        }));
      },
      saveDeskQuestion: (turnId, questionId) => {
        set((s) => ({
          conversations: s.conversations.map((c) => ({
            ...c,
            turns: c.turns.map((t) => (t.id === turnId ? { ...t, deskQuestionId: questionId } : t)),
          })),
        }));
      },
    }),
    {
      name: 'ocbc-ask-history',
      version: 2,
      // v1 answers predate data panels; without this the saved chats crash the Ask page.
      migrate: (persisted, version) => {
        if (version >= 2) return persisted as ChatState;
        const state = persisted as SavedChats;
        const conversations = state.conversations.map((c) => ({
          ...c,
          turns: c.turns.map((t) => ({ ...t, answer: t.answer && { ...t.answer, panels: t.answer.panels ?? [] } })),
        }));
        return { activeId: state.activeId, conversations };
      },
      partialize: (s) => ({ activeId: s.activeId, conversations: s.conversations.slice(0, MAX_SAVED).map(settle) }),
    },
  ),
);
